if (!customElements.get("cart-discount-selector")) {
  class CartDiscountSelector extends HTMLElement {
    constructor() {
      super();

      this.select = null;
      this.applyButton = null;
      this.removeButton = null;
      this.copyButton = null;
      this.codeText = null;
      this.percentage = null;
      this.heading = null;
      this.message = null;
      this.exclusions = null;
      this.status = null;
      this.idleState = null;
      this.loadingState = null;
      this.copiedMessage = null;

      this.onSelectChange = this.onSelectChange.bind(this);
      this.onApply = this.onApply.bind(this);
      this.onRemove = this.onRemove.bind(this);
      this.onCopy = this.onCopy.bind(this);
    }

    connectedCallback() {
      this.cacheElements();
      this.bindEvents();
      this.syncSelectedOffer();
      this.syncAppliedState();
      if (this.closest("cart-drawer")) {
        this.syncOfferFromCartPage();
      }
    }

    async syncOfferFromCartPage() {
      try {
        const root = window.Shopify?.routes?.root || "/";
        const cartUrl = root.replace(/\/$/, "") + "/cart";
        const response = await fetch(cartUrl, {
          headers: { Accept: "text/html" },
          credentials: "same-origin",
        });
        if (!response.ok || !this.isConnected) return;
        const html = await response.text();
        if (!this.isConnected) return;
        const documentFromCart = new DOMParser().parseFromString(html, "text/html");
        const source = documentFromCart.querySelector(".pnoe-cart-promo");
        const sourceOption = source?.querySelector("[data-discount-select] option");
        const code = sourceOption?.value?.trim();
        if (!code || !this.select || !this.select.options.length) return;

        const percentage = sourceOption.dataset.percentage || "";
        const eyebrow = source.querySelector(".pnoe-cart-promo__copy small")?.textContent?.trim() || "";
        const localOption = this.select.options[0];
        localOption.value = code;
        localOption.textContent = code;
        localOption.dataset.percentage = percentage;
        localOption.dataset.heading = sourceOption.dataset.heading || "";
        localOption.dataset.message = sourceOption.dataset.message || "";
        localOption.dataset.exclusions = sourceOption.dataset.exclusions || "";

        const badge = this.closest(".pnoe-cart-drawer__promo-compact")
          ?.querySelector(".pnoe-cart-drawer__promo-badge");
        if (badge && percentage) badge.textContent = "-" + percentage + "%";
        const label = this.closest(".pnoe-cart-drawer__promo-compact")
          ?.querySelector(".pnoe-cart-drawer__promo-copy strong");
        if (label && eyebrow) label.textContent = eyebrow;
        const displayedCode = this.closest(".pnoe-cart-drawer__promo-compact")
          ?.querySelector(".pnoe-cart-drawer__promo-copy small b");
        if (displayedCode) displayedCode.textContent = code;
        this.syncSelectedOffer();
      } catch (error) {
        console.warn("PNOE: Cart promo sync unavailable; retaining configured drawer offer.", error);
      }
    }

    disconnectedCallback() {
      this.unbindEvents();

      if (this.copyTimer) {
        window.clearTimeout(this.copyTimer);
        this.copyTimer = null;
      }
    }

    cacheElements() {
      this.select = this.querySelector("[data-discount-select]");
      this.applyButton = this.querySelector("[data-apply-discount]");
      this.removeButton = this.querySelector("[data-remove-discount]");
      this.copyButton = this.querySelector("[data-copy-discount-code]");
      this.codeText = this.querySelector("[data-discount-code-text]");
      this.percentage = this.querySelector("[data-discount-percentage]");
      this.heading = this.querySelector("[data-discount-heading]");
      this.message = this.querySelector("[data-discount-message]");
      this.exclusions = this.querySelector("[data-discount-exclusions]");
      this.status = this.querySelector("[data-discount-status]");
      this.idleState = this.querySelector("[data-discount-button-idle]");
      this.loadingState = this.querySelector(
        "[data-discount-button-loading]",
      );
      this.copiedMessage = this.querySelector("[data-discount-copied]");
    }

    bindEvents() {
      this.select?.addEventListener("change", this.onSelectChange);
      this.applyButton?.addEventListener("click", this.onApply);
      this.removeButton?.addEventListener("click", this.onRemove);
      this.copyButton?.addEventListener("click", this.onCopy);
    }

    unbindEvents() {
      this.select?.removeEventListener("change", this.onSelectChange);
      this.applyButton?.removeEventListener("click", this.onApply);
      this.removeButton?.removeEventListener("click", this.onRemove);
      this.copyButton?.removeEventListener("click", this.onCopy);
    }

    get selectedOption() {
      if (!this.select) return null;

      return this.select.options[this.select.selectedIndex] || null;
    }

    get selectedCode() {
      return this.selectedOption?.value?.trim() || "";
    }

    get activeCode() {
      return this.dataset.activeCode?.trim() || "";
    }

    normalizeCode(code) {
      return String(code || "")
        .trim()
        .toLowerCase();
    }

    syncSelectedOffer() {
      const option = this.selectedOption;

      if (!option) return;

      const code = option.value || "";
      const percentage = option.dataset.percentage || "";
      const heading = option.dataset.heading || "";
      const message = option.dataset.message || "";
      const exclusions = option.dataset.exclusions || "";

      if (this.percentage) {
        this.percentage.textContent = percentage
          ? `-${percentage}% OFF`
          : "";
      }

      if (this.heading) {
        this.heading.textContent = heading;
      }

      if (this.message) {
        this.message.textContent = message;
      }

      if (this.exclusions) {
        this.exclusions.textContent = exclusions;
      }

      if (this.codeText) {
        this.codeText.textContent = code;
      }

      if (this.copyButton) {
        this.copyButton.dataset.discountCode = code;
        this.copyButton.setAttribute(
          "aria-label",
          `Αντιγραφή κωδικού ${code}`,
        );
      }

      if (this.applyButton) {
        this.applyButton.dataset.discountCode = code;
      }

      this.syncAppliedState();
      this.setStatus("");
    }

    syncAppliedState() {
      const isApplied =
        this.normalizeCode(this.selectedCode) ===
          this.normalizeCode(this.activeCode) &&
        Boolean(this.activeCode);

      this.dataset.applied = String(isApplied);

      if (this.idleState) {
        this.idleState.textContent = isApplied
          ? "Εφαρμόστηκε"
          : this.dataset.applyLabel || "Εφαρμογή";
      }

      if (this.applyButton) {
        this.applyButton.disabled = isApplied;
        this.applyButton.setAttribute(
          "aria-disabled",
          String(isApplied),
        );
      }

      if (this.removeButton) {
        this.removeButton.classList.toggle(
          "hidden",
          !this.activeCode,
        );
      }
    }

    onSelectChange() {
      this.syncSelectedOffer();
    }

    async onApply(event) {
      event.preventDefault();

      const code = this.selectedCode;

      if (!code) return;

      await this.updateDiscount(code);
    }

    async onRemove(event) {
      event.preventDefault();
      await this.updateDiscount("");
    }

    async onCopy(event) {
      event.preventDefault();

      const code =
        event.currentTarget?.dataset?.discountCode ||
        this.selectedCode;

      if (!code) return;

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(code);
        } else {
          const textarea = document.createElement("textarea");

          textarea.value = code;
          textarea.setAttribute("readonly", "");
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";

          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          textarea.remove();
        }

        if (this.copiedMessage) {
          this.copiedMessage.classList.remove("hidden");

          if (this.copyTimer) {
            window.clearTimeout(this.copyTimer);
          }

          this.copyTimer = window.setTimeout(() => {
            this.copiedMessage?.classList.add("hidden");
          }, 2000);
        }
      } catch (error) {
        console.error("Discount code copy failed:", error);
      }
    }

    getSectionsToRender() {
      const drawer = document.getElementById("Cart-Drawer");
      const drawerSectionId =
        drawer?.dataset?.sectionId ||
        this.dataset.sectionId ||
        "cart-drawer";

      return [
        {
          id: "Cart-Drawer",
          section: drawerSectionId,
        },
        {
          id: "Cart-Icon-Bubble",
          section: "cart-icon-bubble",
        },
      ];
    }

    getCartUpdateUrl() {
      const root = window.Shopify?.routes?.root || "/";

      return `${root}cart/update.js`;
    }

    async updateDiscount(code) {
      if (this.dataset.loading === "true") return;

      const sectionsToRender = this.getSectionsToRender();
      const sections = sectionsToRender.map((item) => item.section);

      this.setLoading(true);
      this.setStatus("");

      try {
        const response = await fetch(this.getCartUpdateUrl(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            discount: code,
            sections,
            sections_url: window.location.pathname,
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result?.description ||
              result?.message ||
              this.dataset.errorLabel ||
              "Η έκπτωση δεν εφαρμόστηκε.",
          );
        }

        if (!result?.sections) {
          throw new Error(
            this.dataset.errorLabel ||
              "Δεν ήταν δυνατή η ανανέωση του καλαθιού.",
          );
        }

        const onCartPage =
          !this.closest("#Cart-Drawer") &&
          Boolean(document.querySelector("[data-cart-page]"));

        if (onCartPage) {
          window.location.reload();
          return;
        }

        this.renderSections(result.sections);

        document.dispatchEvent(
          new CustomEvent(
            window.PUB_SUB_EVENTS?.cartUpdate || "cart-update",
            {
              detail: {
                source: "cart-discount-selector",
                discountCode: code,
                cart: result,
              },
            },
          ),
        );
      } catch (error) {
        console.error("Cart discount error:", error);
        this.setLoading(false);
        this.setStatus(
          error?.message ||
            this.dataset.errorLabel ||
            "Η έκπτωση δεν εφαρμόστηκε.",
          true,
        );
      }
    }

    renderSections(sections = {}) {
      const drawer = document.getElementById("Cart-Drawer");

      if (drawer) {
        const drawerSectionId =
          drawer.dataset.sectionId ||
          this.dataset.sectionId ||
          "cart-drawer";

        const drawerHtml = sections[drawerSectionId];

        if (drawerHtml) {
          const parsed = new DOMParser().parseFromString(
            drawerHtml,
            "text/html",
          );

          const sourceDrawer = parsed.getElementById("Cart-Drawer");

          if (sourceDrawer) {
            this.replaceDrawerPart(
              drawer,
              sourceDrawer,
              "[data-cart-drawer-content]",
            );

            this.replaceDrawerPart(
              drawer,
              sourceDrawer,
              "[data-cart-drawer-summary]",
            );

            this.replaceDrawerPart(
              drawer,
              sourceDrawer,
              ".cart-drawer__header",
            );

            if (
              window.webvista?.initLazyImageAnimation
            ) {
              window.webvista.initLazyImageAnimation(drawer);
            }

            if (
              typeof drawer.refreshUpsell === "function"
            ) {
              drawer.refreshUpsell();
            }
          }
        }
      }

      const iconHtml = sections["cart-icon-bubble"];
      const iconContainer =
        document.getElementById("Cart-Icon-Bubble");

      if (iconHtml && iconContainer) {
        const parsed = new DOMParser().parseFromString(
          iconHtml,
          "text/html",
        );

        const source =
          parsed.querySelector("#Cart-Icon-Bubble") ||
          parsed.querySelector(".shopify-section");

        if (source) {
          if (source.id === "Cart-Icon-Bubble") {
            iconContainer.innerHTML = source.innerHTML;
          } else {
            const nested =
              source.querySelector("#Cart-Icon-Bubble");

            iconContainer.innerHTML = nested
              ? nested.innerHTML
              : source.innerHTML;
          }
        }
      }
    }

    replaceDrawerPart(drawer, sourceDrawer, selector) {
      const target = drawer.querySelector(selector);
      const source = sourceDrawer.querySelector(selector);

      if (!target || !source) return;

      target.replaceWith(source.cloneNode(true));
    }

    setLoading(isLoading) {
      this.dataset.loading = String(isLoading);

      if (this.applyButton) {
        this.applyButton.disabled = isLoading;
        this.applyButton.setAttribute(
          "aria-busy",
          String(isLoading),
        );
      }

      if (this.removeButton) {
        this.removeButton.disabled = isLoading;
      }

      if (this.select) {
        this.select.disabled = isLoading;
      }

      if (this.idleState) {
        this.idleState.classList.toggle("hidden", isLoading);
      }

      if (this.loadingState) {
        this.loadingState.classList.toggle("hidden", !isLoading);
        this.loadingState.classList.toggle("flex", isLoading);
      }
    }

    setStatus(message, isError = false) {
      if (!this.status) return;

      this.status.textContent = message;
      this.status.classList.toggle("text-red-600", isError);
      this.status.classList.toggle(
        "text-foreground/70",
        !isError,
      );
    }
  }

  customElements.define(
    "cart-discount-selector",
    CartDiscountSelector,
  );
}